import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { fadeInOutAnimation } from '@app/core/animations';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { UiModule } from '@app/ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { createWidget } from '@typeform/embed';
import '@typeform/embed/build/css/widget.css';

@Component({
  selector: 'rk-tutorial-modal',
  imports: [UiModule, TranslatePipe, FontAwesomeModule],
  templateUrl: './tutorial-modal.component.html',
  styleUrls: ['./tutorial-modal.component.scss'],
  animations: [fadeInOutAnimation],
})
export class TutorialModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('typeformContainer', { static: true }) typeformContainer!: ElementRef;
  showButtons = false;
  private readonly dialogRef = inject(MatDialogRef<TutorialModalComponent>);
  private readonly store = inject(Store);

  private typeformWidget: any;

  constructor(@Inject(MAT_DIALOG_DATA) public data: { url: string }) {}

  ngAfterViewInit() {
    if (this.data.url && this.typeformContainer) {
      // extract form ID and parameters from Typeform URL
      const { formId, parameters } = this.extractFormIdAndParameters(this.data.url);

      if (formId) {
        this.typeformWidget = createWidget(formId, {
          container: this.typeformContainer.nativeElement,
          height: '100%',
          width: '100%',
          autoResize: true,
          hidden: parameters, // pass url parameters to typeform
          onReady: () => {
            this.showButtons = true;
          },
        });
      }
    }

    // tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'ModalPublishingTutorial',
        component: 'ModalPublishingTutorial',
      }),
    );

    this.store.dispatch(new LogEvent('Button Clicked'));
  }

  validateTutorial() {
    this.dialogRef.close(true);

    // tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'ButtonValidateTutorial',
        component: 'ModalPublishingTutorial',
      }),
    );

    this.store.dispatch(new LogEvent('Button Clicked'));
  }

  closeTutorial() {
    this.dialogRef.close(false);

    // tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'ButtonCloseTutorial',
        component: 'ModalPublishingTutorial',
      }),
    );

    this.store.dispatch(new LogEvent('Button Clicked'));
  }

  ngOnDestroy() {
    if (this.typeformWidget && this.typeformWidget.unmount) {
      this.typeformWidget?.unmount();
    }
  }

  private extractFormIdAndParameters(url: string): {
    formId: string | null;
    parameters: Record<string, string>;
  } {
    const patterns = [
      /(?:[\w-]+\.)?typeform\.com\/to\/([a-zA-Z0-9]+)/,
      /admin\.typeform\.com\/form\/([a-zA-Z0-9]+)/,
    ];

    let formId: string | null = null;
    const parameters: Record<string, string> = {};

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        // extract just the form ID part, ignoring query parameters and fragments
        formId = match[1].split(/[#?]/)[0];
        break;
      }
    }

    // if it's already just a form ID
    if (!formId && /^[a-zA-Z0-9]+$/.test(url)) {
      formId = url;
    }

    // extract URL parameters after #
    const hashIndex = url.indexOf('#');
    if (hashIndex !== -1) {
      const paramString = url.substring(hashIndex + 1);
      const urlParams = new URLSearchParams(paramString);

      urlParams.forEach((value, key) => {
        parameters[key] = value;
      });
    }

    return { formId, parameters };
  }
}
